// Pricing plans with free trial
export interface PricingPlan {
  id: string;
  name: string;
  pricePerMonthUSD: number;
  pricePerYearUSD: number;
  callsPerMonth: number;
  price: string;
  paymentLinks: {
    test: string;
    production: string;
  };
  subtitle: string;
  features: string[];
  priceIds: string[]; // Add this field for Stripe price IDs (multiple per plan for each environment)
  sale?: PricingPlanSale;
}

export interface PricingPlanSale {
  label: string;
  price: string;
  pricePerMonthUSD: number;
  pricePerYearUSD: number;
  promoCode: string;
}

export interface PricingDisplay {
  showSale: boolean;
  label?: string;
  price: string;
  pricePerMonthUSD: number;
  pricePerYearUSD: number;
  basePrice: string;
  basePricePerMonthUSD: number;
  basePricePerYearUSD: number;
}

interface PricingContext {
  isAuthenticated: boolean;
  /** Settings-only: logged-in users with incomplete/inactive billing can still get the sale */
  incompletePaymentSale?: boolean;
}

const isProductionEnvironment = () => process.env.NODE_ENV === 'production';

export const isPricingSaleEnabled = (): boolean => {
  return process.env.NEXT_PUBLIC_PRICING_SALE_ENABLED === 'true';
};

/** Settings sale for accounts that never finished (or lost) payment setup */
export const isIncompletePaymentSaleEligible = (status?: string | null): boolean => {
  return status === 'missing_payment_info' || status === 'inactive';
};

export const shouldShowPricingSale = ({
  isAuthenticated,
  incompletePaymentSale,
}: PricingContext): boolean => {
  if (!isPricingSaleEnabled()) return false;
  if (!isAuthenticated) return true;
  return Boolean(incompletePaymentSale);
};

export const getPlanDisplayPrice = (
  plan: PricingPlan,
  context: PricingContext
): PricingDisplay => {
  const showSale = shouldShowPricingSale(context) && Boolean(plan.sale);

  if (showSale && plan.sale) {
    return {
      showSale: true,
      label: plan.sale.label,
      price: plan.sale.price,
      pricePerMonthUSD: plan.sale.pricePerMonthUSD,
      pricePerYearUSD: plan.sale.pricePerYearUSD,
      basePrice: plan.price,
      basePricePerMonthUSD: plan.pricePerMonthUSD,
      basePricePerYearUSD: plan.pricePerYearUSD,
    };
  }

  return {
    showSale: false,
    price: plan.price,
    pricePerMonthUSD: plan.pricePerMonthUSD,
    pricePerYearUSD: plan.pricePerYearUSD,
    basePrice: plan.price,
    basePricePerMonthUSD: plan.pricePerMonthUSD,
    basePricePerYearUSD: plan.pricePerYearUSD,
  };
};

// Helper function to get the appropriate payment link based on environment
export const getPaymentLink = (plan: PricingPlan): string => {
  const isProduction = isProductionEnvironment();
  return isProduction ? plan.paymentLinks.production : plan.paymentLinks.test;
};

export const getPlanPaymentLink = (
  plan: PricingPlan,
  context: PricingContext
): string => {
  if (shouldShowPricingSale(context) && plan.sale) {
    const paymentUrl = new URL(getPaymentLink(plan));
    paymentUrl.searchParams.set('prefilled_promo_code', plan.sale.promoCode);
    return paymentUrl.toString();
  }

  return getPaymentLink(plan);
};

const SALE_LABEL = 'Limited-time sale';
export const PRICING_SALE_PROMO_CODE = 'SAVE40';

const createSaleConfig = (
  pricePerMonthUSD: number,
  price: string
): PricingPlanSale => ({
  label: SALE_LABEL,
  price,
  pricePerMonthUSD,
  pricePerYearUSD: pricePerMonthUSD * 10,
  promoCode: PRICING_SALE_PROMO_CODE,
});

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$49.95/month',
    pricePerMonthUSD: 49.95,
    pricePerYearUSD: 49.95 * 10,
    callsPerMonth: 50,
    paymentLinks: {
      test: 'https://buy.stripe.com/test_28EcMY84X5eo4ZWgTU8Zq01',
      production: 'https://buy.stripe.com/5kQaEXfuWaug4626Kgak001'
    },
    subtitle: 'Perfect for small businesses getting started with AI phone answering',
    features: [
      'Up to 50 minutes of calls per month',
      'Answer all missed calls on your behalf',
      'Share booking links, website, and contact info via text',
      'Answer frequently-asked questions from your FAQ',
      'Superior conversational AI experience',
      'Automatically block robocalls and spam',
      'Call summaries via email',
      'Basic lead qualification and information collection',
      'Email support'
    ],
    priceIds: [
      'price_1RaUJJ4YH2wsjGIfPsM6sJlf', // non-prod price ID
      'price_1RkKFcG2x2V5csT9Olp5TR6U' // prod price ID
    ],
    sale: createSaleConfig(29.97, '$29.97/month'),
  },
  {
    id: 'growth',
    name: 'Growth',
    price: '$99.95/month',
    pricePerMonthUSD: 99.95,
    pricePerYearUSD: 99.95 * 10,
    callsPerMonth: 200,
    paymentLinks: {
      test: 'https://buy.stripe.com/test_6oUcMY5WP22cbok1Z08Zq02',
      production: 'https://buy.stripe.com/fZueVd1E6dGs1XU7Okak000'
    },
    subtitle: 'Best AI phone answering solution for growing businesses',
    features: [
      'Up to 200 minutes of calls per month',
      'Everything in Starter plan',
      'Remove Greet branding',
      'Call summaries via SMS',
      'Schedule appointments',
      'Advanced lead qualification and data collection',
      'Multi-language support (5+ languages)',
      'More AI receptionist personalities',
      'Advanced call analytics and reporting dashboard',
      'Priority support with faster response times'
    ],
    priceIds: [
      'price_1RaUJg4YH2wsjGIfFHF9OlCr', // non-prod price ID
      'price_1RkKFaG2x2V5csT9LPZrPtJH' // prod price ID
    ],
    sale: createSaleConfig(59.97, '$59.97/month'),
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$199.95/month',
    pricePerMonthUSD: 199.95,
    pricePerYearUSD: 199.95 * 10,
    callsPerMonth: 500,
    paymentLinks: {
      test: 'https://buy.stripe.com/test_7sYcMY2KD5eoakgdHI8Zq00',
      production: 'https://buy.stripe.com/eVqaEX3Me0TG462c4Aak002'
    },
    subtitle: 'Virtual AI receptionist for busy teams and enterprises',
    features: [
      'Up to 500 minutes of calls per month',
      'Everything in Growth plan',
      'All AI receptionist personalities',
      'Advanced CRM and calendar integrations',
      'Multi-language support (10+ languages)',
      'Dedicated support'
    ],
    priceIds: [
      'price_1RaUKC4YH2wsjGIfulB7Fx0f', // non-prod price ID
      'price_1RkKFeG2x2V5csT9U87TIpWb' // prod price ID
    ],
    sale: createSaleConfig(119.97, '$119.97/month'),
  }
];

// Helper function to find a plan by ID
export const getPlanById = (id: string): PricingPlan | undefined => {
  return PRICING_PLANS.find(plan => plan.id === id);
};

// Helper function to get plans as a record for backward compatibility
export const PRICING_PLANS_RECORD: Record<string, PricingPlan> = PRICING_PLANS.reduce((acc, plan) => {
  acc[plan.id] = plan;
  return acc;
}, {} as Record<string, PricingPlan>); 

export const PUBLIC_BASE_STARTING_PRICE = PRICING_PLANS[0].price;
export const PUBLIC_SALE_STARTING_PRICE = PRICING_PLANS[0].sale?.price || PUBLIC_BASE_STARTING_PRICE;
export const PUBLIC_SALE_PRICE_RANGE = '$29.97-$119.97/month';
export const PUBLIC_BASE_PRICE_RANGE = '$49.95-$199.95/month';
